import { useSeason } from '@/lib/season';
import { LEAF_MAPLE, LEAF_MAPLE_STEM, LEAF_OVAL, LEAF_OVAL_VEIN } from './shapes';

/**
 * Украшение карты подписки по сезону. Лежит под текстом карты (в разметке раньше
 * слоёв `tl-layer`) и держится там, где на карте пусто: верхний край над строкой
 * с названием, правая кромка между верхней строкой и сроком. Цифры и название
 * не перекрывает ни один вариант. «Схема» рисует то же линиями (index.css).
 */
export function SeasonCardAccent() {
  const season = useSeason();
  if (!season) return null;
  if (season === 'newyear') return <Garland />;
  if (season === 'winter') return <SnowCap />;
  if (season === 'halloween') return <Cobweb />;
  return <Leaves />;
}

const BULBS = ['#ff5a5f', '#ffd05a', '#5ad1ff', '#7ef09a'];
const SWAGS = 3;

/** Точка на провисающем проводе: квадратичная кривая от 2 до 2 через 22 (вершина на 12).
 *  Слой ровно 20px, как viewBox провода: иначе лампочки висели бы мимо него. */
const wireY = (t: number) => 2 * (1 - t) ** 2 + 44 * (1 - t) * t + 2 * t ** 2;

function Garland() {
  const bulbs: { x: number; y: number; c: string; d: number }[] = [];
  let k = 0;
  for (let s = 0; s < SWAGS; s++) {
    for (const t of [0.2, 0.5, 0.8]) {
      bulbs.push({ x: ((s + t) / SWAGS) * 100, y: wireY(t), c: BULBS[k % BULBS.length], d: (k * 0.37) % 2.4 });
      k++;
    }
  }
  const d = Array.from({ length: SWAGS }, (_, s) => {
    const a = (s / SWAGS) * 100;
    const b = ((s + 1) / SWAGS) * 100;
    return `${s === 0 ? `M${a},2 ` : ''}Q${(a + b) / 2},22 ${b},2`;
  }).join(' ');
  return (
    <span aria-hidden className="tl-season pointer-events-none absolute inset-x-0 top-0 block h-5" data-kind="garland">
      <svg className="absolute inset-0 size-full" viewBox="0 0 100 20" preserveAspectRatio="none">
        <path d={d} fill="none" stroke="rgba(20,24,20,0.55)" strokeWidth="1.2" vectorEffect="non-scaling-stroke" data-wire />
      </svg>
      {bulbs.map((b, i) => (
        <i
          key={i}
          className="tl-bulb absolute block"
          style={{ left: `${b.x}%`, top: `${b.y}px`, ['--bulb' as string]: b.c, animationDelay: `${b.d}s` }}
        />
      ))}
    </span>
  );
}

function SnowCap() {
  return (
    <span aria-hidden className="tl-season pointer-events-none absolute inset-x-0 top-0 block h-4" data-kind="snow">
      <svg className="absolute inset-0 size-full" viewBox="0 0 300 16" preserveAspectRatio="none">
        <path
          data-fill
          d="M0 0H300V7C292 9 287 6 279 8C270 10 266 7 258 8.5C252 9.5 250 13 246 13C242 13 241 9.5 236 9C226 8 220 11 210 9C200 7 194 10 184 9.5C176 9 172 7 163 8.5C155 10 151 8 143 8C134 8 130 11 121 10C113 9 108 7 99 8C92 9 90 14.5 85.5 14.5C81 14.5 81 10 75 9C66 7.5 61 10 52 9C44 8 39 6 30 7.5C22 9 16 7 9 8C5 8.5 2 8 0 7Z"
          fill="rgba(255,255,255,0.94)"
        />
      </svg>
    </span>
  );
}

function Cobweb() {
  // Паутина в правом верхнем углу: лучи из угла и провисающие дуги между ними.
  const rays = [0, 18, 38, 58, 78, 90].map((deg) => (deg * Math.PI) / 180);
  const R = 92;
  const at = (r: number, a: number) => [100 - Math.sin(a) * r * (100 / R), Math.cos(a) * r * (100 / R)] as const;
  const rings = [26, 48, 70, 90].map((r) =>
    rays
      .slice(0, -1)
      .map((a, i) => {
        const [x1, y1] = at(r, a);
        const [x2, y2] = at(r, rays[i + 1]);
        const [cx, cy] = at(r * 0.82, (a + rays[i + 1]) / 2);
        return `${i === 0 ? `M${x1},${y1} ` : ''}Q${cx},${cy} ${x2},${y2}`;
      })
      .join(' '),
  );
  return (
    <span aria-hidden className="tl-season pointer-events-none absolute right-0 top-0 block" data-kind="web">
      <svg className="absolute right-0 top-0 size-[84px]" viewBox="0 0 100 100" fill="none" stroke="rgba(255,255,255,0.24)" strokeWidth="0.9">
        {rays.map((a, i) => {
          const [x, y] = at(R, a);
          return <path key={i} d={`M100,0 L${x},${y}`} />;
        })}
        {rings.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </svg>
      {/* Паук на нитке: спускается ниже статуса, в пустую полосу справа. */}
      <span className="tl-spider absolute right-[30px] top-0 block">
        <span className="absolute left-1/2 top-0 block h-[58px] w-px -translate-x-1/2 bg-white/35" />
        <svg className="absolute left-1/2 top-[54px] size-[18px] -translate-x-1/2" viewBox="0 0 24 24" data-spider>
          <g stroke="#17121d" strokeWidth="1.4" strokeLinecap="round" fill="none" data-legs>
            <path d="M9 11L4 8 2 10M9 13H3.5L2 15M9.5 14.5L5 18 4.5 20M15 11L20 8 22 10M15 13H20.5L22 15M14.5 14.5L19 18 19.5 20" />
          </g>
          <circle cx="12" cy="9.5" r="2.6" fill="#17121d" data-fill />
          <ellipse cx="12" cy="14.5" rx="3.6" ry="4.4" fill="#17121d" data-fill />
          <circle cx="11" cy="9" r=".6" fill="#ff8a3d" />
          <circle cx="13" cy="9" r=".6" fill="#ff8a3d" />
        </svg>
      </span>
    </span>
  );
}

function Leaves() {
  const leaves = [
    { d: LEAF_MAPLE, v: LEAF_MAPLE_STEM, c: '#d4572a', s: 40, r: -28, x: -12, y: 0 },
    { d: LEAF_OVAL, v: LEAF_OVAL_VEIN, c: '#e9b23c', s: 28, r: 32, x: 10, y: 26 },
    { d: LEAF_MAPLE, v: LEAF_MAPLE_STEM, c: '#e0912f', s: 26, r: 64, x: -2, y: 44 },
  ];
  return (
    <span aria-hidden className="tl-season pointer-events-none absolute right-0 top-[30%] block h-[80px] w-[56px]" data-kind="leaves">
      {leaves.map((l, i) => (
        <svg
          key={i}
          className="absolute"
          style={{ width: l.s, height: l.s, right: l.x, top: l.y, transform: `rotate(${l.r}deg)` }}
          viewBox="0 0 24 24"
        >
          <path d={l.d} fill={l.c} data-fill />
          <path d={l.v} stroke="rgba(80,30,10,0.45)" strokeWidth="0.8" fill="none" />
        </svg>
      ))}
    </span>
  );
}
